import { Link } from 'react-router-dom';
import { Icon } from './Icon.js';

export type CrumbRole = 'module' | 'record' | 'plain';

export interface Crumb {
  label: string;
  /** Router path; the crumb is a link where present. */
  to?: string;
  /**
   * `module`: the fixed point of a trail — never shortened or scrolled out of reach.
   * `record`: a name the app cannot bound — the first to give way, by ellipsis; its full value is
   * reachable on the page it links to (02-patterns §2.1). Defaults to `plain`, which behaves as module.
   */
  role?: CrumbRole;
}

export interface BreadcrumbProps {
  /** Ancestors only — never the current page's own title (02-patterns §2.1). */
  items: readonly Crumb[];
  /**
   * Reserve the row with nothing in it: the same element laid out and made invisible, so the
   * title's position is derived from the crumb's own box, never computed (02-patterns §2.1).
   */
  placeholder?: boolean;
  className?: string;
}

/** One line at every width; record crumbs give way first, module crumbs survive whole. */
export function Breadcrumb({ items, placeholder = false, className }: BreadcrumbProps) {
  if (placeholder || items.length === 0) {
    return (
      <nav className={['breadcrumb', 'breadcrumb--placeholder', className].filter(Boolean).join(' ')} aria-hidden="true">
        <ol className="breadcrumb__list" role="list">
          <li className="breadcrumb__item breadcrumb__item--plain is-last">
            <span className="breadcrumb__text">&nbsp;</span>
          </li>
        </ol>
      </nav>
    );
  }
  return (
    <nav className={['breadcrumb', className].filter(Boolean).join(' ')} aria-label="Breadcrumb">
      <ol className="breadcrumb__list" role="list">
        {items.map((item, index) => {
          const role = item.role ?? 'plain';
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className={`breadcrumb__item breadcrumb__item--${role}${last ? ' is-last' : ''}`}>
              {last ? <Icon name="chevron-left" size={14} className="breadcrumb__back" /> : null}
              {item.to ? (
                <Link to={item.to} className="breadcrumb__text breadcrumb__link" title={role === 'record' ? item.label : undefined}>{item.label}</Link>
              ) : (
                <span className="breadcrumb__text" title={role === 'record' ? item.label : undefined}>{item.label}</span>
              )}
              {!last ? <span className="breadcrumb__separator" aria-hidden="true"><Icon name="chevron-right" size={12} /></span> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
